import { useEffect, useRef } from "react";

/**
 * Manejo de foco de un diálogo modal (Modal, Drawer) — A11Y-11.
 *
 * - Al abrir: foco al primer elemento enfocable del cuerpo (o al que tenga
 *   `autoFocus`, que React ya enfocó); si el cuerpo no tiene ninguno, al
 *   título (que debe llevar `tabIndex={-1}`).
 * - Mientras está abierto: Tab / Shift+Tab ciclan dentro del panel.
 * - Al cerrar: el foco vuelve al elemento que lo tenía antes de abrir.
 *
 * El diálogo se renderiza en un portal, así que la trampa escucha `keydown`
 * en el propio panel y no en `window` (no pelea con otros diálogos).
 */
const ENFOCABLES = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

function enfocables(raiz: HTMLElement): HTMLElement[] {
  return Array.from(raiz.querySelectorAll<HTMLElement>(ENFOCABLES)).filter(
    (el) =>
      el.tabIndex >= 0 &&
      !el.closest("[inert]") &&
      el.getAttribute("aria-hidden") !== "true",
  );
}

export function useDialogFocus(
  open: boolean,
  panelRef: React.RefObject<HTMLElement>,
  bodyRef: React.RefObject<HTMLElement>,
  titleRef: React.RefObject<HTMLElement>,
): void {
  const previo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previo.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const panel = panelRef.current;
    // Foco inicial: si un hijo ya lo tomó con autoFocus, se respeta.
    if (panel && !panel.contains(document.activeElement)) {
      const body = bodyRef.current;
      const primero = body ? enfocables(body)[0] : undefined;
      (primero ?? titleRef.current ?? panel).focus();
    }

    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Tab" || !panel) return;
      const lista = enfocables(panel);
      const first = lista[0];
      const last = lista[lista.length - 1];
      if (!first || !last) {
        e.preventDefault();
        return;
      }
      const activo = document.activeElement;
      if (e.shiftKey && (activo === first || !panel.contains(activo))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (activo === last || !panel.contains(activo))) {
        e.preventDefault();
        first.focus();
      }
    };
    panel?.addEventListener("keydown", onKey);

    return () => {
      panel?.removeEventListener("keydown", onKey);
      const destino = previo.current;
      // Si el disparador ya no existe (p. ej. la fila se borró), no se fuerza.
      if (destino && document.contains(destino)) destino.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
